"use client"

import styled from "@emotion/styled"
import { Button } from "@mui/material"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import { extractProperty, getLinkHrefFromMultiUrlPickerItem, getUmbracoMediaUrl } from "utils"
import { BRUSH_UNDERLINE_URI } from "../brushUnderline"
import { handleAnchorScroll } from "../scrollToAnchor"
import { LANDING_ELEMENTS, useLandingAnalytics } from "../analytics"

type Props = {
  data: any
}

const MAX_LOGOS = 4

// --- Reveal-once-on-scroll hook (AC: initial animation fires once per page load) ---

function useInViewOnce<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [inView, setInView] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el || inView) return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true)
          observer.disconnect()
        }
      },
      { threshold: 0.2 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [inView])

  return { ref, inView }
}

// --- Styled Components ---

const Section = styled.section`
  padding: 40px 20px;
  background-color: var(--comp-bg);
  color: var(--comp-text);
  overflow: hidden;

  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 90px 120px;
  }
`

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  max-width: 1200px;
  margin: 0 auto;

  /* row-reverse keeps the image first in JSX (so mobile column stacks image on top per AC)
     while placing it on the left on desktop */
  ${(props) => props.theme.breakpoints.up("md")} {
    flex-direction: row-reverse;
    align-items: center;
    gap: 64px;
  }
`

/* image slides in from the far (left) edge; content from the near (right) edge in RTL */
const ImageWrapper = styled.div<{ $in: boolean }>`
  position: relative;
  width: 100%;
  max-width: 335px;
  /* landscape 540/446 ratio on all breakpoints: mobile 335x276 (ASH-1789),
     desktop 540x446 (ASH-1787) */
  aspect-ratio: 540 / 446;
  flex-shrink: 0;
  border-radius: 8px;
  overflow: hidden;
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateX(${({ $in }) => ($in ? "0" : "-40px")});
  transition: opacity 0.6s ease, transform 0.6s ease;

  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 540px;
  }
`

const Content = styled.div<{ $in: boolean }>`
  flex: 1;
  min-width: 0;
  text-align: start;
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateX(${({ $in }) => ($in ? "0" : "40px")});
  transition: opacity 0.6s ease, transform 0.6s ease;
`

/* base title weight is 400; bold words from CMS get the brush underline (ASH-1785) */
const Headline = styled.h2`
  font-size: 40px;
  font-weight: 400;
  line-height: 1;
  color: var(--comp-heading);
  margin: 0;

  p {
    margin: 0;
    display: inline;
  }
  strong {
    font-weight: 700;
    text-decoration: none;
    display: inline-block;
    position: relative;
  }
  strong::after {
    content: "";
    position: absolute;
    inset-inline: 0;
    bottom: -4px;
    height: 6px;
    background-color: currentColor;
    -webkit-mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
    mask: url("${BRUSH_UNDERLINE_URI}") no-repeat center / 100% 100%;
  }

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 60px;
  }
`

const BodyText = styled.p`
  font-size: 16px;
  font-weight: 400;
  /* Figma 1990:11439 body line-height 27/18 = 1.5 */
  line-height: 1.5;
  color: var(--comp-text);
  margin: 16px 0 0;

  ${(props) => props.theme.breakpoints.up("md")} {
    font-size: 18px;
  }
`

const CtaButton = styled(Button)`
  && {
    /* 36px tall with 16px side padding per Figma 1990:11439 (button 77×36) */
    height: 36px;
    /* 16px gap from the text above (ASH-1791) */
    margin-top: 16px;
    padding: 0 16px;
    /* rectangular per Figma (ASH-1786) */
    border-radius: 8px;
    /* CTA text: 16px mobile, 14px desktop (ASH-1784) */
    font-size: 16px;
    font-weight: 700;
    text-transform: none;
    background-color: var(--inner-bg);
    color: var(--inner-text);

    ${(props) => props.theme.breakpoints.up("md")} {
      font-size: 14px;
    }

    &:hover {
      background-color: var(--inner-bg);
      opacity: 0.9;
    }

    &:focus-visible {
      outline: 2px dashed var(--inner-text);
      outline-offset: 2px;
    }
  }
`

/* logos appear with a slight delay after the content (AC).
   nowrap keeps all logos on one row on mobile (ASH-1790); 40px above (ASH-1791) */
const LogosRow = styled.div<{ $in: boolean }>`
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  gap: 12px;
  margin-top: 40px;
  opacity: ${({ $in }) => ($in ? 1 : 0)};
  transform: translateY(${({ $in }) => ($in ? "0" : "16px")});
  transition: opacity 0.5s ease 0.3s, transform 0.5s ease 0.3s;

  ${(props) => props.theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

/* fixed logo height so the row is uniform (ASH-1788): 50 mobile / 77 desktop per Figma */
const Logo = styled.img`
  height: 50px;
  width: auto;
  min-width: 0;
  object-fit: contain;

  ${(props) => props.theme.breakpoints.up("md")} {
    height: 77px;
  }
`

// --- Parsing ---

type LogoItem = { url: string; alt: string }

function parseLogos(data: any): LogoItem[] {
  // Multiple Media Picker → mediaItems array
  const media = extractProperty(data, "logos", true) as any[] | undefined
  if (Array.isArray(media) && media.length) {
    return media.map((m) => ({ url: m.url, alt: m.altText || "" })).filter((l) => l.url)
  }
  // Fallback: Block List of logo items, each holding an image/logo media picker
  const blocks = extractProperty(data, "logos") as any[] | ""
  if (Array.isArray(blocks)) {
    return blocks
      .map((b) => {
        const img = (extractProperty(b, "image", true) || extractProperty(b, "logo", true)) as
          | any[]
          | undefined
        return { url: img?.[0]?.url || "", alt: img?.[0]?.altText || "" }
      })
      .filter((l) => l.url)
  }
  return []
}

// --- Main Component ---

function About({ data }: Props) {
  const analytics = useLandingAnalytics()
  const { ref, inView } = useInViewOnce<HTMLDivElement>()

  const headline = extractProperty(data, "headline") as string | ""
  const text = extractProperty(data, "text") as string | ""
  const ctaLinks = extractProperty(data, "ctaButton") as any[] | ""
  const cta = Array.isArray(ctaLinks) ? ctaLinks[0] : undefined
  const image = extractProperty(data, "image", true) as any[] | undefined
  const imageUrl = image?.[0]?.url || ""
  const logos = parseLogos(data).slice(0, MAX_LOGOS)

  if (!headline && !text && !imageUrl) return null

  return (
    <Section ref={ref}>
      <Inner>
        {imageUrl && (
          <ImageWrapper $in={inView}>
            <Image
              src={getUmbracoMediaUrl(imageUrl)}
              fill
              alt={image?.[0]?.altText || ""}
              style={{ objectFit: "cover" }}
            />
          </ImageWrapper>
        )}
        <Content $in={inView}>
          {headline && <Headline dangerouslySetInnerHTML={{ __html: headline }} />}
          {text && <BodyText>{text}</BodyText>}
          {cta?.url && cta?.name && (
            <CtaButton
              variant="contained"
              href={getLinkHrefFromMultiUrlPickerItem(cta)}
              onClick={(e: React.MouseEvent) => {
                analytics.ctaClick(cta.name, LANDING_ELEMENTS.aboutClosing)
                handleAnchorScroll(e, getLinkHrefFromMultiUrlPickerItem(cta))
              }}
              disableElevation
              {...(cta.target === "_blank" ? { target: "_blank", rel: "noopener noreferrer" } : {})}
            >
              {cta.name}
            </CtaButton>
          )}
          {logos.length > 0 && (
            <LogosRow $in={inView}>
              {logos.map((logo, i) => (
                <Logo key={i} src={getUmbracoMediaUrl(logo.url)} alt={logo.alt} />
              ))}
            </LogosRow>
          )}
        </Content>
      </Inner>
    </Section>
  )
}

export default About
